import { ReactElement, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { SVG_MUI } from "@ryde/assets";
import { EntityProperty, RydeUiUnit } from "@ryde/shared";

import { EntityNestedCommandContext } from "./entity-nested-command.context";
import { EntityProperties } from "./entity-properties";
import { EntityPropertiesControlsStyled } from "./entity-properties.styled";

interface IEntityPropertiesWithControlsProps {
  entity?: RydeUiUnit;
  data: EntityProperty[];
  /** Shown in the same row, right after the buttons (the row's info bubbles). */
  toolbarExtra?: ReactNode;
}

/** One history row's fields with small buttons that open or close every nested block (metadata, stations, ...) of it. */
export const EntityPropertiesWithControls = ({
  entity,
  data,
  toolbarExtra,
}: IEntityPropertiesWithControlsProps): ReactElement => {
  const { t } = useTranslation();
  const [command, setCommand] = useState({
    isExpanded: undefined as boolean | undefined,
    version: 0,
  });

  const onExpand = useEvent(() =>
    setCommand(prev => ({ isExpanded: true, version: prev.version + 1 })),
  );
  const onCollapse = useEvent(() =>
    setCommand(prev => ({ isExpanded: false, version: prev.version + 1 })),
  );
  const commandValue = useMemo(() => command, [command]);

  return (
    <EntityPropertiesControlsStyled>
      <div className="entityPropertiesControls__toolbar">
        <div className="entityPropertiesControls__buttons">
          <button
            type="button"
            className="entityPropertiesControls__button"
            title={t("auditLog.changes.expandDetails")}
            aria-label={t("auditLog.changes.expandDetails")}
            onClick={onExpand}
          >
            <SVG_MUI.ArrowStatExpand3SVG />
          </button>
          <button
            type="button"
            className="entityPropertiesControls__button"
            title={t("auditLog.changes.collapseDetails")}
            aria-label={t("auditLog.changes.collapseDetails")}
            onClick={onCollapse}
          >
            <SVG_MUI.ArrowStatCollapse3SVG />
          </button>
        </div>
        {toolbarExtra && (
          <div className="entityPropertiesControls__extra">{toolbarExtra}</div>
        )}
      </div>
      <EntityNestedCommandContext.Provider value={commandValue}>
        <EntityProperties entity={entity} data={data} />
      </EntityNestedCommandContext.Provider>
    </EntityPropertiesControlsStyled>
  );
};
